// ═══════════════════════════════════════════════════════════════════
// ACCUEIL PREMIUM ABRACADABATI — zone blanche du cockpit (desktop)
// Inspiration : Airbnb / Apple / Claude — immersif, fluide, simple.
// On est devant le centre commercial virtuel, Abra nous accueille.
// (L'ancien accueil HomePage reste intact — retour en arrière = 1 ligne)
// ═══════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Mic, ArrowRight, Sparkles, Wrench, FileText, Store, HandHeart,
  Handshake, BadgePercent, Bot,
} from 'lucide-react';

// Image hébergée chez nous (frontend/public/images) : servie par le CDN Vercel
// en même temps que la page, préchargée depuis index.html → affichage immédiat.
// (Avant : PNG ~2 Mo sur le CDN Emergent, qui arrivait après la page.)
const IMAGE_CENTRE_COMMERCIAL = '/images/centre-commercial-abracadabati.webp';

const ACTIONS_RAPIDES = [
  { icon: Wrench, label: 'Trouver un artisan', path: '/artisans' },
  { icon: FileText, label: 'Recevoir 5 devis', path: '/decrire-projet' },
  { icon: Store, label: 'Acheter à un pro', path: '/produits' },
  { icon: HandHeart, label: 'Entre particuliers', path: '/entre-particuliers' },
];

const PROMESSES = [
  {
    icon: BadgePercent,
    titre: 'Zéro commission',
    texte: 'Un vrai centre commercial : les pros louent leur boutique, leurs prix restent les leurs.',
  },
  {
    icon: Handshake,
    titre: 'Sans intermédiaire',
    texte: 'En direct avec les pros : tu parles au vrai artisan, personne ne s’interpose.',
  },
  {
    icon: Bot,
    titre: 'Piloté par Abra',
    texte: 'Ton IA t’accompagne : devis, recherche, suivi de projet — à la voix ou à l’écrit.',
  },
];

export default function AccueilBatiPremium() {
  const navigate = useNavigate();
  const [projet, setProjet] = useState('');

  const demanderAAbra = (e) => {
    e?.preventDefault();
    try {
      if (projet.trim()) sessionStorage.setItem('abra_projet_brouillon', projet.trim());
    } catch {}
    navigate('/decrire-projet');
  };

  return (
    <div className="w-full">
      {/* ═══ HERO IMMERSIF — devant le centre commercial ═══ */}
      {/* L'image s'affiche ENTIÈRE (ciel compris) — le bloc Abra se pose dessus, en bas */}
      <section className="relative rounded-3xl overflow-hidden mx-1">
        <img
          src={IMAGE_CENTRE_COMMERCIAL}
          alt="AbracadaBati — le 1er centre commercial virtuel du Bâtiment"
          className="w-full h-auto block"
          loading="eager"
          fetchPriority="high"
        />
        {/* Voile pour la lisibilité */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,22,44,0.72) 0%, rgba(10,22,44,0.38) 38%, rgba(10,22,44,0.55) 72%, rgba(10,22,44,0.82) 100%)',
          }}
        />

        {/* ═══ Enseignes « allumées » (idée Romain) : une seconde copie de l'image,
            visible uniquement sur les lettres et le logo → vraies couleurs pleines,
            pendant que le voile assombrit tout le reste. Respiration très douce. ═══ */}
        <motion.img
          src={IMAGE_CENTRE_COMMERCIAL}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-auto pointer-events-none"
          style={{
            WebkitMaskImage:
              'radial-gradient(ellipse 16% 4% at 56% 11%, black 50%, transparent 80%), radial-gradient(ellipse 33% 10% at 52% 25%, black 45%, transparent 75%), radial-gradient(ellipse 33% 6% at 51% 39%, black 55%, transparent 80%), radial-gradient(ellipse 6% 18% at 11% 50%, black 50%, transparent 80%), radial-gradient(ellipse 6% 18% at 90% 47%, black 50%, transparent 80%)',
            maskImage:
              'radial-gradient(ellipse 16% 4% at 56% 11%, black 50%, transparent 80%), radial-gradient(ellipse 33% 10% at 52% 25%, black 45%, transparent 75%), radial-gradient(ellipse 33% 6% at 51% 39%, black 55%, transparent 80%), radial-gradient(ellipse 6% 18% at 11% 50%, black 50%, transparent 80%), radial-gradient(ellipse 6% 18% at 90% 47%, black 50%, transparent 80%)',
          }}
          animate={{ opacity: [0.88, 1, 0.88] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Bloc Abra : la barre vient REMPLACER le « Bienvenue chez toi » de l'image */}
        <div
          className="absolute left-1/2 z-10 w-full max-w-3xl px-8 flex flex-col items-center text-center"
          style={{ top: '57%', transform: 'translate(-50%, -50%)' }}
        >
          {/* L'image porte l'enseigne et le slogan — une seule ligne pour Abra */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="text-lg text-white/90"
            style={{ textShadow: '0 1px 12px rgba(0,0,0,0.45)' }}
          >
            Abra, ton IA, t'accueille — décris ton projet, elle s'occupe du reste.
          </motion.p>

          {/* Barre Abra */}
          <motion.form
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.24 }}
            onSubmit={demanderAAbra}
            className="mt-8 w-full"
          >
            <div className="flex items-center gap-2 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl pl-4 pr-2 py-2 border border-white/60">
              <Sparkles size={20} className="text-[#F97316] flex-shrink-0" />
              <input
                value={projet}
                onChange={(e) => setProjet(e.target.value)}
                placeholder="Décris ton projet ou pose une question à Abra…"
                className="flex-1 bg-transparent outline-none text-[15px] text-[#1a2a4a] placeholder:text-gray-400 py-2"
                data-testid="abra-home-input"
              />
              <button
                type="button"
                onClick={demanderAAbra}
                title="Parler à Abra"
                className="p-2.5 rounded-xl text-[#1E3A5F] hover:bg-gray-100 transition"
              >
                <Mic size={19} />
              </button>
              <button
                type="submit"
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-white text-[14px] font-semibold shadow-md hover:brightness-110 transition"
                style={{ background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)' }}
              >
                Demander <ArrowRight size={16} />
              </button>
            </div>
          </motion.form>

          {/* Boutons rapides */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.34 }}
            className="mt-6 flex flex-wrap items-center justify-center gap-2.5"
          >
            {ACTIONS_RAPIDES.map(({ icon: Icon, label, path }) => (
              <button
                key={path}
                onClick={() => navigate(path)}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-[13.5px] font-medium text-white border border-white/25 bg-white/10 backdrop-blur-md hover:bg-white/25 hover:scale-[1.03] transition"
              >
                <Icon size={15} className="text-orange-300" />
                {label}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ LES 3 PROMESSES — posées sur le parking (chevauchement du hero) ═══ */}
      <section className="max-w-5xl mx-auto px-6 pb-12 -mt-24 relative z-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PROMESSES.map(({ icon: Icon, titre, texte }, i) => (
            <motion.div
              key={titre}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className="bg-white rounded-2xl p-6 border border-gray-100"
              style={{ boxShadow: '0 14px 44px rgba(15,27,61,0.16)' }}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(232,101,10,0.10)' }}>
                <Icon size={20} style={{ color: '#E8650A' }} />
              </div>
              <h3 className="text-[16px] font-bold text-[#1a2a4a] mb-1.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {titre}
              </h3>
              <p className="text-[13.5px] leading-relaxed text-gray-500">{texte}</p>
            </motion.div>
          ))}
        </div>

        {/* CTA fondateur */}
        <motion.button
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          onClick={() => navigate('/pro')}
          className="mt-10 w-full flex items-center justify-between px-7 py-5 rounded-2xl text-white hover:brightness-110 transition"
          style={{ background: 'linear-gradient(120deg, #1E3A5F 0%, #25477a 55%, #F97316 140%)' }}
        >
          <div className="text-left">
            <p className="text-[17px] font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Professionnel du bâtiment ?
            </p>
            <p className="text-[13.5px] text-white/75 mt-0.5">
              Loue ta boutique en 72 h — zéro commission, agents IA inclus.
            </p>
          </div>
          <span className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/15 border border-white/25 text-[14px] font-semibold flex-shrink-0">
            Découvrir <ArrowRight size={16} />
          </span>
        </motion.button>
      </section>
    </div>
  );
}
